/**
 * ลานประลอง (data/duelGround.ts): open challenges in the duelling ground. Standing near the ring, the player
 * challenges (ท้าประลอง, `duelChallenge`) someone about it: one of the ring's regulars (arena roster builds standing
 * on the spectator steps), an offline body (systems/offlineWork.ts), or a fellow wanderer. In this single-player
 * build the one challenged answers by a draw from the World's `Rng` (a server would ask the other player).
 *
 * Taken up, the bout runs on the ring system's `Duel` (systems/arena.ts): both are set down on the ring's marks,
 * 3-2-1, then fought by hand (not the arena's auto-fight) until one falls to a tenth of their health; leaving the
 * ring's floor gives the bout away; nothing is lost, and the result sends everyone back whole. The one challenged
 * steps out of the world for the bout (a fighter in their likeness takes the ring) and comes back after it.
 * The ring master at the gate tells how it works; the ladder (L) and the arena (O) are fought in this ring too.
 */
import { ARENA_ROSTER, type ArenaBuild } from '../data/arena';
import { DUEL_GROUND, duelGroundIn, nearRing, shiftDuelGround, type DuelGroundDef } from '../data/duelGround';
import { coreOf } from '../region/core';
import { LADDER } from '../data/ladder';
import { sparSpotsIn, SPAR_TIERS } from '../data/sparring';
import { rankName } from '../cultivation';
import { sparBeaten, sparLocked, sparWin, sparWinsLeft } from '../sparring';
import { getArt, getSkill } from '../data/skills';
import { arenaBody } from '../arena';
import type { ActivityMode } from '../data/modes';
import { modesOf } from './modes';
import { freeAt, nearestFloor } from '../path';
import { dist, type Duel, type Entity } from '../world';
import type { SystemHost, WorldSystem } from './types';

declare module '../world' {
  interface Duel {
    /**
     * An open challenge in the duelling ground (systems/duelGround.ts): its ring, who was challenged, and the
     * sparring tier when it was a sparring partner (data/sparring.ts).
     */
    open?: { ring: DuelGroundDef; figure: number; spar?: number };
  }
  interface Entity {
    /** A ring regular (the arena roster build it fights with), for the challenge prompt. */
    ringRegular?: number;
    /** A sparring partner (คู่ฝึก): its tier (index into data/sparring.ts `SPAR_TIERS`). */
    sparTier?: number;
  }
}

export type DuelGroundCommand = { t: 'duelChallenge'; id: number; mode?: ActivityMode };

/** Events (part of `WorldEvent`): a challenge answered (`accepted`), or why it could not be made (`reason`). */
export type DuelGroundEvent = { type: 'duelChallenge'; id: number; accepted: boolean; reason?: string };

const SYSTEMS = new WeakMap<object, DuelGroundSystem>();

/** The World's duelling-ground system (for the client's prompt and for tests). */
export const duelGroundOf = (w: object): DuelGroundSystem | null => SYSTEMS.get(w) ?? null;

/** An arena roster build by power rank (an offline body's), strongest at rank 40 and up. */
const buildForRank = (rank: number): ArenaBuild =>
  ARENA_ROSTER[Math.max(0, Math.min(ARENA_ROSTER.length - 1, Math.round((1 - rank / 40) * (ARENA_ROSTER.length - 1))))]!;

export class DuelGroundSystem implements WorldSystem {
  readonly name = 'duelGround';
  ring: DuelGroundDef | null = null;
  /** The ring master's entity id, or -1. */
  master = -1;
  /** Who said no, and the World time they may be asked again. */
  private readonly refused = new Map<number, number>();
  /** The one challenged, set aside for the bout. */
  private away: Entity | null = null;

  /** The mode of the challenge being answered / the bout under way. */
  private mode: ActivityMode = 'npc';

  constructor(private readonly h: SystemHost) {}

  readonly commands = {
    duelChallenge: (c: { id: number; mode?: ActivityMode }) => this.challenge(c.id, c.mode),
  };

  setup(): void {
    const w = this.h.world;
    SYSTEMS.set(w, this);
    const g0 = duelGroundIn(w.map.zone);
    if (!g0) return;
    // The ring's data is in the town's own metres; in a region the town sits at the core's origin.
    const g = shiftDuelGround(g0, coreOf(w.map));
    this.ring = g;
    const gate = this.floor(g0.gate[0], g0.gate[1]);
    if (gate)
      this.master = this.h.add({
        kind: 'npc',
        x: gate.x,
        y: gate.y,
        name: 'ผู้คุมลาน',
        title: g.name,
        sprite: 'guardOfficer',
        facing: 1,
      }).id;
    for (const r of g0.regulars) {
      const b = ARENA_ROSTER[r.roster];
      const at = this.floor(r.at[0], r.at[1]);
      if (!b || !at) continue;
      const e = this.h.add({ kind: 'npc', x: at.x, y: at.y, name: b.name, title: 'นักประลองประจำลาน', sprite: b.sprite });
      e.ringRegular = r.roster;
    }
    // The sparring partners, one a tier, in their row.
    sparSpotsIn(w.map.zone).forEach((s, k) => {
      const t = SPAR_TIERS[k];
      const at = t && this.floor(s[0], s[1]);
      if (!t || !at) return;
      const e = this.h.add({
        kind: 'npc',
        x: at.x,
        y: at.y,
        name: t.person.name,
        title: `${t.name} · แนะนำ${rankName(t.rank)}`,
        sprite: t.person.sprite,
        facing: 1,
      });
      e.sparTier = k;
    });
  }

  private floor(x: number, y: number): { x: number; y: number } | null {
    const m = this.h.world.map;
    const o = coreOf(m);
    x += o.x;
    y += o.y;
    if (x < 0 || y < 0 || x >= m.w || y >= m.h) return null;
    if (freeAt(m, x, y)) return { x, y };
    const a = nearestFloor(m, x, y, 3);
    return a ? { x: a[0], y: a[1] } : null;
  }

  /** Someone the player may challenge: a ring regular, an offline body at work, or a fellow wanderer. */
  challengeable(e: Entity | undefined): e is Entity {
    if (!e || e.dead || e === this.h.world.player || this.h.world.inParty(e)) return false;
    return (
      e.ringRegular !== undefined ||
      e.sparTier !== undefined ||
      (!!e.offline && !e.sack) ||
      e.kind === 'rival' ||
      e.kind === 'player'
    );
  }

  /** Why the player cannot challenge `e` now (Thai), or null. */
  challengeBlock(e: Entity | undefined): string | null {
    const w = this.h.world;
    const p = w.player;
    const g = this.ring;
    if (!g) return 'ที่นี่ไม่มีลานประลอง';
    if (!this.challengeable(e)) return 'ท้าประลองคนนี้ไม่ได้';
    if (w.duel) return 'อยู่ระหว่างประลองอยู่แล้ว';
    if (p.dead || p.jailT > 0 || p.carrying != null) return 'ตอนนี้ประลองไม่ได้';
    if (p.combatT > 0) return 'ยังอยู่ในการต่อสู้';
    if (!nearRing(g, p)) return 'ต้องอยู่ที่ลานประลอง';
    if (dist(p, e) > DUEL_GROUND.reach) return 'อยู่ไกลเกินไป';
    if ((this.refused.get(e.id) ?? -1) > w.time) return `${e.name}เพิ่งปฏิเสธไป`;
    if (e.sparTier !== undefined && sparLocked(w.progress, e.sparTier))
      return `ต้องชนะ${SPAR_TIERS[e.sparTier - 1]!.name}ก่อน`;
    return null;
  }

  private challenge(id: number, asked?: ActivityMode): void {
    const w = this.h.world;
    const e = w.get(id);
    const why = this.challengeBlock(e);
    if (why || !e) {
      if (why) this.h.notice(why);
      this.h.emit({ type: 'duelChallenge', id, accepted: false, reason: why ?? undefined });
      return;
    }
    // The mode is chosen at the challenge; with no player found `pvp` falls back to NPCs (the stub).
    this.mode = modesOf(w)?.resolve('duel', asked) ?? 'npc';
    // A sparring partner always takes it up; anyone else answers by the World's draw.
    const odds = e.ringRegular !== undefined ? DUEL_GROUND.acceptRegular : DUEL_GROUND.acceptOffline;
    const accepted = e.sparTier !== undefined || this.h.random() < odds;
    this.h.emit({ type: 'duelChallenge', id, accepted });
    if (!accepted) {
      this.refused.set(id, w.time + DUEL_GROUND.askAgain);
      this.h.emit({ type: 'bark', id, text: 'วันนี้ข้าไม่สะดวก ไว้คราวหน้าเถิด' });
      return this.h.notice(`${e.name}ปฏิเสธคำท้า`);
    }
    this.h.emit({ type: 'bark', id, text: 'ได้! มาประลองกันที่ลาน' });
    this.begin(e);
  }

  /** The bout: both on their marks, the one challenged as a fighter of their build, then the ring's countdown. */
  private begin(e: Entity): void {
    const w = this.h.world;
    const g = this.ring!;
    const p = w.player;
    const build: ArenaBuild =
      e.sparTier !== undefined
        ? SPAR_TIERS[e.sparTier]!.person
        : e.ringRegular !== undefined
          ? ARENA_ROSTER[e.ringRegular]!
          : { ...buildForRank(e.offline?.rank ?? 10), name: e.name, sprite: e.sprite };
    this.h.stopMeditating();
    this.h.dismount();
    const d: Duel = {
      ladder: null,
      phase: 'countdown',
      t: LADDER.countdown,
      foeId: -1,
      foe: { name: e.name, rating: e.offline?.rank ?? 0 },
      arenaRank: 0,
      autoWas: w.auto.on,
      back: { x: p.x, y: p.y },
      won: false,
      mode: this.mode,
      open: { ring: g, figure: e.id, ...(e.sparTier !== undefined ? { spar: e.sparTier } : {}) },
    };
    // The one challenged steps out of the world for the bout; a fighter in their likeness takes the ring.
    w.entities.delete(e.id);
    this.away = e;
    const [a, b] = g.marks;
    Object.assign(p, { x: a[0], y: a[1], z: 0, path: null, cast: null });
    this.h.input.engage = false;
    w.auto.on = false;
    const foe = this.h.add({
      kind: 'rival',
      x: b[0],
      y: b[1],
      name: e.name,
      title: e.title,
      sprite: build.sprite,
      speed: p.speed,
      facing: -1,
      rival: {
        homeX: (a[0] + b[0]) / 2,
        homeY: (a[1] + b[1]) / 2,
        aggroT: 0,
        skillT: 1.5,
        skills: getArt(build.art)
          .skills.filter((id) => ['attack', 'break', 'power'].includes(getSkill(id).kind))
          .slice(0, 4),
      },
    });
    Object.assign(foe, arenaBody(build));
    this.h.face(p, foe);
    d.foeId = foe.id;
    w.duel = d;
    this.h.log('sys', `ท้าประลอง: ${e.name} รับคำท้า`);
    this.h.emit({ type: 'ladder', phase: 'countdown', ladder: null, foe: e.name });
  }

  /** The ring master tells how the ring works; a regular invites a challenge. */
  talk(npc: Entity): boolean {
    if (npc.id === this.master) {
      this.h.emit({
        type: 'dialog',
        npcId: npc.id,
        text: 'ลานนี้เปิดให้ทุกคน ยืนใกล้ลานแล้วท้าประลองคนที่อยู่รอบๆ ได้ สู้กันในลานจนอีกฝ่ายเหลือพลังชีวิตหนึ่งในสิบ ไม่มีใครตาย ไม่เสียของ แต่ออกนอกลานเมื่อไรถือว่ายอมแพ้ · บันไดประลอง (L) และประลองยุทธ (O) ก็สู้กันที่ลานนี้',
        action: null,
      });
      return true;
    }
    if (npc.sparTier !== undefined) {
      const k = npc.sparTier;
      const t = SPAR_TIERS[k]!;
      const pr = this.h.world.progress;
      const pay = sparBeaten(pr, k)
        ? `ชนะซ้ำได้ ${t.repeat.gold} ตำลึง ${t.repeat.cultivation} แต้มฝึกปรือ (วันนี้เหลือ ${Math.max(0, sparWinsLeft(pr))} ครั้ง)`
        : `ชนะครั้งแรกได้ ${t.first.gold} ตำลึง ${t.first.cultivation} แต้มฝึกปรือ${t.first.title ? ' และฉายา' : ''}`;
      const text = sparLocked(pr, k)
        ? `${t.name} (${t.style}) · ต้องชนะ${SPAR_TIERS[k - 1]!.name}ก่อน · แนะนำ${rankName(t.rank)}`
        : `${t.name} (${t.style}) · แนะนำ${rankName(t.rank)} · ${pay} · ท้าประลองข้าได้ที่ลาน`;
      this.h.emit({ type: 'dialog', npcId: npc.id, text, action: null });
      return true;
    }
    if (npc.ringRegular !== undefined) {
      this.h.emit({ type: 'dialog', npcId: npc.id, text: 'มาดูฝีมือกันหน่อยไหม ท้าประลองข้าได้เลย', action: null });
      return true;
    }
    return false;
  }

  /** A sparring bout decided in the player's favour pays its tier's reward, once per bout. */
  private paid: Duel | null = null;
  private pay(): void {
    const w = this.h.world;
    const d = w.duel;
    if (!d?.open || d.open.spar === undefined || d.phase !== 'result' || this.paid === d) return;
    this.paid = d;
    if (!d.won) return;
    const t = SPAR_TIERS[d.open.spar]!;
    const r = sparWin(w.progress, d.open.spar);
    if (!r) return this.h.notice(`ชนะ${t.name} · วันนี้รับรางวัลครบแล้ว`);
    const got = this.h.grantReward(r.reward);
    this.h.notice(`${r.first ? 'ชนะ' : 'ชนะซ้ำ'}${t.name}: ${got}`);
    this.h.emit({ type: 'progressChanged' });
  }

  /** Once the bout is over (the ring system clears it), the one challenged comes back. */
  step(): void {
    this.pay();
    const e = this.away;
    if (!e) return;
    const w = this.h.world;
    if (w.duel?.open?.figure === e.id) return;
    this.away = null;
    w.entities.set(e.id, e);
  }
}
